// 문서 상태 배지 — 저장 버튼 옆에서 "지금 저장하면 무엇이 어긋난 채로 나가나"를 한 숫자로 보인다.
//
// 왜 여기인가: 저작 경고는 전부 **그 패널을 열어야만** 보였다(조회 파라미터는 캔버스 빈 곳 클릭 후
// 속성 패널 스크롤, 변수는 변수 편집기 안). 문서 수명주기 버튼(저장·공개) 옆이 저작자가 "내보내기
// 전 마지막으로 보는 자리"라, 경고가 눈에 걸리려면 그 자리여야 한다.
//
// 배지는 **막지 않는다** — 숫자와 목록을 보이고, 클릭하면 고칠 자리로 데려다줄 뿐이다(집계 층
// `templateIssues.ts`가 severity를 warn 하나로 두는 것과 같은 정책).

import { useEffect, useRef, useState } from 'react';

import {
  groupTemplateIssues,
  TEMPLATE_ISSUE_KIND_LABEL,
  type TemplateIssue,
} from './templateIssues';

/** hover 요약(title) 한 줄 — 팝오버를 열지 않아도 종류별 개수를 읽게 한다. */
function issuesTitle(issues: TemplateIssue[]): string {
  const groups = groupTemplateIssues(issues);
  return [
    `저작 경고 ${issues.length}건 — 저장은 막지 않습니다.`,
    ...groups.map((g) => `· ${g.title} ${g.issues.length}`),
    '클릭하면 내역과 이동 링크가 열립니다.',
  ].join('\n');
}

/**
 * 저작 경고 배지 + 내역 팝오버.
 *
 * 경고가 0이면 **렌더하지 않는다** — "0건"을 상시로 두면 배지가 배경이 되어, 정작 숫자가 붙었을 때
 * 눈에 걸리지 않는다.
 */
export function TemplateIssuesBadge({
  issues,
  onGo,
}: {
  issues: TemplateIssue[];
  /** 경고 항목 클릭 — 그 경고를 고칠 편집기로 이동시킨다. */
  onGo: (issue: TemplateIssue) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // 바깥 클릭·Esc로 닫기. 팝오버는 모달이 아니라(포커스 트랩 없음) 저작 흐름을 막지 않는다.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // 경고가 사라지면(다 고쳤으면) 열려 있던 팝오버도 닫는다 — 빈 상자가 남지 않게.
  useEffect(() => {
    if (issues.length === 0) setOpen(false);
  }, [issues.length]);

  if (issues.length === 0) return null;

  const groups = groupTemplateIssues(issues);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={issuesTitle(issues)}
        className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-100"
      >
        ⚠ {issues.length}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 max-h-[60vh] w-[26rem] overflow-y-auto rounded-card border border-gray-200 bg-white p-2 shadow-lg">
          <p className="mb-1.5 px-1 text-[11px] leading-snug text-gray-500">
            저장·공개를 막지는 않습니다. 항목을 클릭하면 고칠 자리로 이동합니다.
          </p>
          <div className="space-y-2">
            {groups.map((group) => (
              <div key={group.kind}>
                <div className="px-1 text-[11px] font-semibold text-gray-700">
                  {TEMPLATE_ISSUE_KIND_LABEL[group.kind]}
                  <span className="ml-1 text-gray-400">{group.issues.length}</span>
                </div>
                <ul className="mt-0.5 space-y-0.5">
                  {group.issues.map((issue, i) => (
                    <li key={`${issue.kind}:${issue.catalogKey ?? ''}:${issue.paramName ?? ''}:${issue.variableKey ?? ''}:${issue.bindingKey ?? ''}:${i}`}>
                      <button
                        type="button"
                        onClick={() => {
                          setOpen(false);
                          onGo(issue);
                        }}
                        className="w-full rounded-card-sm px-1.5 py-1 text-left text-[11px] leading-snug text-gray-600 hover:bg-amber-50"
                      >
                        {issue.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
